import { type FC } from "react";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { LabeledCheckbox } from "../LabeledCheckbox";

import type { VirtualClassFormValues } from "./types";

import { t } from "i18n/i18n";
import { Listbox } from "mds/components/Listbox";
import { Tooltip } from "mds/components/Tooltip";
import { useAppSelector } from "store/index";
import { selectDesignatedSection } from "store/selectors";
import { getAvServiceFormItems } from "utils/av";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";

interface VirtualClassFormProps {
  className?: string;
  form: UseFormReturn<VirtualClassFormValues>;
}

export const VirtualClassForm: FC<VirtualClassFormProps> = ({ className, form }) => {
  const currentSection = useAppSelector(selectDesignatedSection);
  const { isArchived } = useIsArchived();

  if (!currentSection) {
    return null;
  }

  const disabled = isLinkedSection(currentSection) || isArchived(currentSection);

  const avEnabled = form.watch("av_enabled");
  const avService = form.watch("av_service");

  return (
    <FormProvider {...form}>
      <div
        className={twMerge(
          "flex flex-col text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
      >
        <div className="flex flex-col gap-2">
          <LabeledCheckbox
            eventName="Section Settings - Virtual Classes toggle"
            registration={form.register("av_enabled", { disabled })}
          >
            {t("settings.section_settings_av_enabled.label")}
          </LabeledCheckbox>

          <Tooltip content={t("settings.section_settings_auto_mute.tooltip")}>
            <LabeledCheckbox
              eventName="Section Settings - Mute students on main room entry toggle"
              registration={form.register("mute_students_on_main_room_entry", {
                disabled: !avEnabled || disabled,
              })}
            >
              {t("settings.section_settings_auto_mute.label")}
            </LabeledCheckbox>
          </Tooltip>

          <LabeledCheckbox
            eventName="Section Settings - Auto record class toggle"
            registration={form.register("auto_recordings_enabled", {
              disabled: !avEnabled || disabled,
            })}
          >
            {t("settings.section_settings_auto_recordings.label")}
          </LabeledCheckbox>

          <label className="flex items-center justify-between">
            <span>{t("settings.section_settings_av_service.label")}</span>

            <input type="hidden" {...form.register("av_service")} />

            <div className="inline-block">
              <Listbox
                disabled={!avEnabled || disabled}
                eventName="Section Settings - AV service selection"
                eventPropertyKey="newAvService"
                items={getAvServiceFormItems()}
                selectedValue={avService}
                onSelectionChange={({ id }) =>
                  form.setValue("av_service", id, { shouldDirty: true })
                }
              />
            </div>
          </label>
        </div>
      </div>
    </FormProvider>
  );
};
